/**
 * Pricing display contract (BL-038).
 *
 * Single source of truth for turning a server-owned `PricingDto` (BL-022)
 * into the already-translated strings every pricing surface renders:
 * struck `listPrice` + bold `currentPrice` + one discount chip + a
 * disclaimer that names the post-beta regular price.
 *
 * Every citizen + business offer-price surface (journey unlock/renewal,
 * subscription plans, sponsorship rate, sponsor modal) routes through this
 * so the beta voice ("Just €1") is identical everywhere — no surface
 * hand-rolls its own strikethrough/chip logic.
 *
 * This is presentation formatting only — the discount DECISION is
 * server-owned (Non-Negotiable #9). It does NOT apply to actual amounts
 * (wallet balance, invoices, transactions, paid amount, refunds, spend
 * projections); those are not list-vs-current and never get strikethrough.
 */

import type { PricingDto } from './billing.service';

/** Minimal translate signature — avoids coupling @tonnie/sdk to @tonnie/locale. */
export type PricingTranslate = (key: string, opts?: Record<string, unknown>) => string;

/**
 * Formats an amount as money IN THE READER'S LOCALE — pass
 * `useLocale().formatCurrency` from @tonnie/locale. (F34 · F49)
 *
 * This module used to own a `formatEuro` that printed `€X.XX` in every locale,
 * so a pt-PT reader saw "€4.99" where their language writes "4,99 €". The
 * formatter is the caller's because only the caller knows the locale, and the
 * SDK stays free of a @tonnie/locale dependency.
 */
export type PricingMoneyFormat = (amount: number) => string;

export interface PricingDisplay {
  /** Formatted current price, e.g. "€1.00" (en) or "1,00 €" (pt-PT). Empty when pricing is absent. */
  currentPriceText: string;
  /** Formatted list price — present (and struck) only when discounted. */
  listPriceText?: string;
  /** Translated discount chip, e.g. "Beta price". Present only when discounted. */
  chipLabel?: string;
  /** Translated disclaimer naming the regular price. Present only when discounted. */
  disclaimerText?: string;
}

/**
 * Map a `PricingDto` to display strings. Returns `{ currentPriceText: '' }`
 * for an absent DTO so callers can branch to a skeleton (offer surfaces must
 * never assert a hardcoded list price — wait for the server).
 */
export function pricingDisplay(
  pricing: PricingDto | undefined | null,
  t: PricingTranslate,
  formatMoney: PricingMoneyFormat
): PricingDisplay {
  if (!pricing) return { currentPriceText: '' };

  const currentPriceText = formatMoney(pricing.currentPrice);
  if (!pricing.discount) return { currentPriceText };

  const listPriceText = formatMoney(pricing.listPrice);
  const chipLabel =
    pricing.discount.mode === 'percentage'
      ? t(pricing.discount.labelKey, { percent: pricing.discount.percentOff })
      : t(pricing.discount.labelKey);

  return {
    currentPriceText,
    listPriceText,
    chipLabel,
    disclaimerText: pricing.disclaimerKey
      ? t(pricing.disclaimerKey, { listPrice: listPriceText })
      : undefined,
  };
}
